import { detectGlobalContext } from "../../utils/engine/globalContextEngine";

function average(values) {
  if (!values.length) {
    return 0;
  }

  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function analyzeExecutionCandles(candles, lookback = 24) {
  if (!Array.isArray(candles) || candles.length < lookback) {
    return null;
  }

  const recent = candles.slice(-lookback);
  const currentPrice = recent[recent.length - 1]?.close ?? null;
  const high = Math.max(...recent.map((candle) => candle.high));
  const low = Math.min(...recent.map((candle) => candle.low));
  const range = Math.max(0, high - low);
  const impulse = recent[recent.length - 1].close - recent[0].open;
  const bodySum = recent.reduce(
    (sum, candle) => sum + Math.abs(candle.close - candle.open),
    0
  );
  const avgRange = average(recent.map((candle) => candle.high - candle.low));
  const efficiency = range > 0 ? Math.abs(impulse) / range : 0;
  const normalizedVolatility = currentPrice
    ? (avgRange / Math.max(1, currentPrice)) * 100
    : 0;
  const direction =
    impulse > 0 ? "BULLISH" : impulse < 0 ? "BEARISH" : "NEUTRAL";

  return {
    currentPrice,
    high,
    low,
    range,
    impulse,
    bodySum,
    avgRange,
    efficiency,
    normalizedVolatility,
    direction,
  };
}

function estimateZoneDistance(candles, direction, lookback = 18) {
  if (!Array.isArray(candles) || candles.length < lookback) {
    return Number.POSITIVE_INFINITY;
  }

  const recent = candles.slice(-lookback);
  const currentPrice = recent[recent.length - 1]?.close;

  if (!Number.isFinite(currentPrice)) {
    return Number.POSITIVE_INFINITY;
  }

  if (direction === "BULLISH") {
    const support = Math.min(...recent.map((candle) => candle.low));
    return Math.abs(currentPrice - support);
  }

  if (direction === "BEARISH") {
    const resistance = Math.max(...recent.map((candle) => candle.high));
    return Math.abs(resistance - currentPrice);
  }

  return Number.POSITIVE_INFINITY;
}

function buildFailure(reason, metrics = {}, globalContext = null) {
  return {
    passed: false,
    score: 0,
    reason,
    hotness: "idle",
    metrics,
    globalContext,
    direction: globalContext?.direction || "NEUTRAL",
  };
}

export function runRadarPrecheck({
  market,
  executionCandles,
  candlesM5,
  candlesM15,
  candlesM30,
}) {
  const globalContext = detectGlobalContext(
    {
      M5: candlesM5,
      M15: candlesM15,
      M30: candlesM30,
    },
    market
  );

  if (!globalContext?.valid) {
    return buildFailure(globalContext?.reason || "Missing higher-timeframe context");
  }

  const execution = analyzeExecutionCandles(executionCandles);

  if (!execution) {
    return buildFailure("Not enough execution candles", {}, globalContext);
  }

  const conflictingDirection =
    execution.direction !== "NEUTRAL" &&
    globalContext.direction !== "NEUTRAL" &&
    execution.direction !== globalContext.direction;
  const zoneDistance = estimateZoneDistance(
    executionCandles,
    globalContext.direction
  );
  const zoneDistanceRatio =
    execution.avgRange > 0 ? zoneDistance / execution.avgRange : Number.POSITIVE_INFINITY;
  const volatilityActive = execution.normalizedVolatility >= 0.02;
  const momentumAcceptable = execution.bodySum > execution.range * 0.55;
  const structureAligned =
    execution.direction !== "NEUTRAL" &&
    execution.direction === globalContext.direction;
  const notRanging = execution.efficiency >= 0.4;
  const priceNearZone = zoneDistanceRatio <= 2.4;

  if (conflictingDirection) {
    return buildFailure(
      "Execution momentum conflicts with higher-timeframe bias",
      {
        zoneDistanceRatio,
        volatility: execution.normalizedVolatility,
        efficiency: execution.efficiency,
      },
      globalContext
    );
  }

  const scoreParts = [
    globalContext.biasConfidence * 32,
    globalContext.strength * 26,
    notRanging ? 12 : 0,
    structureAligned ? 12 : 0,
    priceNearZone ? 10 : 0,
    volatilityActive ? 4 : 0,
    momentumAcceptable ? 4 : 0,
  ];
  const score = Math.round(
    Math.max(0, Math.min(100, scoreParts.reduce((sum, value) => sum + value, 0)))
  );

  const passed =
    globalContext.direction !== "NEUTRAL" &&
    structureAligned &&
    priceNearZone &&
    notRanging &&
    momentumAcceptable;

  return {
    passed,
    score,
    reason: passed ? "Precheck qualified for pipeline analysis" : "Precheck filtered out weak setup",
    hotness: passed ? (score >= 72 ? "hot" : "warm") : "idle",
    metrics: {
      zoneDistanceRatio: Number(zoneDistanceRatio.toFixed(2)),
      volatility: Number(execution.normalizedVolatility.toFixed(4)),
      efficiency: Number(execution.efficiency.toFixed(2)),
      structureAligned,
      volatilityActive,
      momentumAcceptable,
      priceNearZone,
      notRanging,
    },
    globalContext,
    direction: globalContext.direction,
  };
}
